<template>
  <div>
    <CustomDateSelect
      :date="date"
      :time="time"
      :type="type"
      :isDialogOpen="isDialogOpen"
      :minTime="
        isRoundTrip && isOutwardAndReturnSameDay
          ? outwardTime
          : getDefaultMinTime
      "
      :minDate="isRoundTrip && outwardDate ? outwardDate : defaultMinDate"
      :maxDate="defaultMaxDate"
      :workingHoursZoneWeekDays="workingHoursZoneWeekDays"
      :holidays="holidays"
      :timeZone="timeZone"
      :buttonTextClasses="buttonTextClasses"
      @closeDialog="closeDialog"
      @updateTime="updateTime"
      @updateDate="updateDate"
      @updateDateType="updateDateType"
    >
      <template #customDefaultHeader>
        <span v-if="!date" class="date__picker-custom-header textWhite--text"
          >{{ $t("bookingForm.dateSelection.selectReturnDate") }}
        </span>
      </template>
      <template #customBottom="{ invalid }">
        <VRow class="ma-0 px-4 pb-4" align="center">
          <VCol cols="6" class="ma-0 p-0 pr-1">
            <VBtn
              width="100%"
              outlined
              data-test="backToOutwardButton"
              class="button cancel-button"
              @click="$emit('changeStep', 'OutwardDateSelection')"
            >
              <span class="textPrimary--text">
                {{ $t("bookingForm.dateSelection.back") }}
              </span>
            </VBtn>
          </VCol>
          <VCol cols="6" class="ma-0 p-0 pl-1">
            <VBtn
              color="brandingPrimary"
              width="100%"
              class="button"
              data-test="setDateAndTimeButton"
              :class="{ disabled: invalid }"
              @click="apply"
            >
              <span :class="buttonTextClasses">
                {{ $t("bookingForm.dateSelection.apply") }}
              </span>
            </VBtn>
          </VCol>
        </VRow>
      </template>
    </CustomDateSelect>
  </div>
</template>

<script lang="ts">
export default {
  name: 'ReturnDateSelection',
};
</script>

<script setup lang="ts">
import { computed, ref } from 'vue';
import moment from 'moment';
import SConstants from '@slg/web-customer-shared/src/utils/constants';
import CustomDateSelect from '@slg/web-customer-shared/src/components/bookingForm/datesSelection/CustomDateSelect.vue';

const props = defineProps({
  returnState: {
    type: Object,
    default: null,
  },
  outwardTime: {
    type: String,
    default: null,
  },
  outwardDate: {
    type: String,
    default: null,
  },
  isRoundTrip: {
    type: Boolean,
    default: false,
  },
  holidays: {
    type: Array,
    default: () => [],
  },
  minTimeBeforeBooking: {
    type: Number,
    default: null,
  },
  workingHoursZoneWeekDays: {
    type: Array,
    default: () => [],
  },
  defaultMaxDate: {
    type: String,
    default: null,
  },
  defaultMinDate: {
    type: String,
    default: null,
  },
  dateNow: {
    type: Object,
    default: null,
  },
  timeZone: {
    type: String,
    default: null,
  },
  buttonTextClasses: {
    type: String,
    default: 'textWhite--text',
  },
});

const emit = defineEmits(['setReturnDate', 'closeDialog']);

const isDialogOpen = ref(false);
const time = ref(null);
const date = ref(null);
const type = ref(null);
const isOutwardNow = ref(false);

const isOutwardAndReturnSameDay = computed(() => props.outwardDate === date.value);

const getDefaultMinTime = computed(() => {
  const minTime = props.dateNow.clone().tz(props.timeZone);
  return moment(minTime).format(SConstants.TIME_FORMAT.YYYY_MM_DD) ===
    date.value
    ? minTime
        .add(props.minTimeBeforeBooking, 'minutes')
        .format(SConstants.TIME_FORMAT.HH_mm)
    : null;
});

const { date: returnDate, time: returnTime, type: returnType } = props.returnState;
date.value = returnDate;
time.value = returnTime;
type.value = returnType;

function updateDate(newValue) {
  date.value = newValue;
}

function updateDateType(newValue) {
  type.value = newValue;
}

function updateTime(newValue) {
  time.value = newValue;
}

function closeDialog() {
  emit('closeDialog');
}

function apply() {
  const returnDatePayload = {
    date: date.value,
    time: time.value,
    type: type.value,
  };
  emit('setReturnDate', returnDatePayload);
  closeDialog();
}
</script>
<style lang="scss" scoped>
@import "@slg/web-customer-shared/src/style/variables.scss";

.dialog-row {
  width: 100%;
}

.date__picker-custom-header {
  line-height: 12px;
  margin-top: 7px;
  margin-right: 2px;
  position: absolute;
  top: 42px;
  left: 14px;
  font-size: $fs-32;
  color: var(--v-textWhite-base) !important;
}

.cancel-button {
  span {
    color: var(--v-textPrimary-base) !important;
  }
}
</style>
